import React, { FunctionComponent, useState, memo, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react-lite';

import { Icon, Text, Button } from '../Primitives';
import { Card, CardContent } from '../ui/card';

import { DefaultGroupImage } from '../ui/default-group-image';

import { IGroup } from '../../interface/Group';

import { userStore } from '../../stores/UserStore';
import { groupStore } from '../../stores/GroupStore';

type Props = {
  group: IGroup;
};

const GroupCard: FunctionComponent<Props> = observer(({ group }) => {
  const { joinGroup } = groupStore;
  const { account } = userStore;
  const history = useHistory();
  const [imgError, setImgError] = useState(false);

  // Memoize handlers
  const handleCardClick = useCallback(() => {
    history.push(`/groups/${group.groupProfile.uri}`);
  }, [history, group.groupProfile.uri]);

  const handleJoinClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    joinGroup(group.id);
  }, [joinGroup, group.id]);

  const {
    members,
    groupProfile: { sport, picture, name, privacy, location },
  } = group;

  const isPublic = privacy === 'PUBLIC';
  const self = members?.find((m) => m.profileId === account.profileId);
  const isRequested = self?.status === 'PENDING' ? true : false;
  const isApproved = self?.status === 'ACTIVE';
  return (
    <Card 
      className="overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
      onClick={handleCardClick}
    >
      <div className="relative w-full h-[152px]">
        {!imgError && picture ? (
          <img
            src={picture}
            alt={name}
            className="w-full h-full object-cover object-center"
            onError={() => setImgError(true)}
          />
        ) : (
          <DefaultGroupImage className="w-full h-full" />
        )}
        <Text
          variant="lb.sm"
          className="text-white self-end p-2 absolute bottom-0 left-0"
          style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
        >
          {sport}
        </Text>
      </div>
      <CardContent className="p-3">
        <Text truncate variant="lb.md" className="mb-1">
          {name}
        </Text>
        <div className="flex items-center">
          {!isPublic ? (
            <>
              <Icon icon="lock-alt" />
              <Text className="ml-1">Private group</Text>
            </>
          ) : (
            <Text>
              {(() => {
                const count = members?.length || group.totalPeople || 0;
                return count > 1 ? `${count} players` : '1 player';
              })()}
            </Text>
          )}
        </div>
        <Text className="ml-2 text-muted-foreground">{location?.name}</Text>
      </CardContent>
      <div className="px-3 pb-3">
        <Button
          fullWidth
          variant="outline"
          disabled={isApproved || isRequested}
          onClick={handleJoinClick}
        >
          {isRequested ? 'Requested' : isApproved ? 'Joined' : 'Join'}
        </Button>
      </div>
    </Card>
  );
});

export default memo(GroupCard);
